import React, { useState } from 'react';
import { systemSettings } from '../../data/adminMock';
import { Settings, Shield, Bell, Code, Save, RefreshCw, KeyRound, Eye, EyeOff, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { authAPI } from '../../services/api';

const ChangePasswordCard = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: '', message: '' });

    if (newPassword.length < 8) {
      setFeedback({ type: 'error', message: 'New password must be at least 8 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', message: 'New password and confirmation do not match' });
      return;
    }

    setSubmitting(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setFeedback({ type: 'success', message: 'Password changed. Use it next time you sign in.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to change password';
      setFeedback({ type: 'error', message: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 mb-6"
      data-testid="change-password-card"
    >
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-5 h-5 text-teal-400" />
        <h3 className="text-lg font-semibold text-white">Change Admin Password</h3>
      </div>
      <p className="text-gray-500 text-sm mb-5">
        Rotates the current signed-in admin&apos;s password. You will not be signed out — use the new password next time.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-gray-300 text-sm mb-2" htmlFor="current-password-input">Current Password</label>
          <div className="relative">
            <input
              id="current-password-input"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-10 text-white outline-none focus:border-teal-400"
              data-testid="current-password-input"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((p) => !p)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white"
              aria-label="Toggle visibility"
              data-testid="toggle-current-password-visibility"
            >
              {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2" htmlFor="new-password-input">New Password</label>
          <div className="relative">
            <input
              id="new-password-input"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 pr-10 text-white outline-none focus:border-teal-400"
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowNew((p) => !p)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white"
              aria-label="Toggle visibility"
              data-testid="toggle-new-password-visibility"
            >
              {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-1">Must be at least 8 characters.</p>
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2" htmlFor="confirm-password-input">Confirm New Password</label>
          <input
            id="confirm-password-input"
            type={showNew ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
            data-testid="confirm-password-input"
          />
        </div>

        {feedback.message && (
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              feedback.type === 'success'
                ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                : 'bg-red-500/10 text-red-400 border border-red-500/30'
            }`}
            data-testid="change-password-feedback"
          >
            {feedback.type === 'success'
              ? <CheckCircle className="w-4 h-4" />
              : <AlertCircle className="w-4 h-4" />}
            <span>{feedback.message}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !currentPassword || !newPassword || !confirmPassword}
          className="flex items-center justify-center gap-2 bg-teal-400 hover:bg-teal-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-black px-4 py-2 rounded-lg font-medium transition-colors"
          data-testid="change-password-submit-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          Change Password
        </button>
      </div>
    </form>
  );
};

const AdminSettings = () => {
  const [settings, setSettings] = useState(systemSettings);
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'api', label: 'API Settings', icon: Code },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">System Settings</h1>
          <p className="text-gray-400">Configure platform settings and preferences</p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors"
        >
          {saved ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Saved!
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Changes
            </>
          )}
        </button>
      </div>

      <div className="flex gap-8">
        {/* Sidebar Tabs */}
        <div className="w-64 space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === tab.id
                  ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
                  : 'text-gray-400 hover:text-white hover:bg-[#1a1a1f]'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          {activeTab === 'general' && (
            <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">General Settings</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Site Name</label>
                  <input
                    type="text"
                    value={settings.general.siteName}
                    onChange={(e) => setSettings({
                      ...settings,
                      general: { ...settings.general, siteName: e.target.value }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Support Email</label>
                  <input
                    type="email"
                    value={settings.general.supportEmail}
                    onChange={(e) => setSettings({
                      ...settings,
                      general: { ...settings.general, supportEmail: e.target.value }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Timezone</label>
                  <select
                    value={settings.general.timezone}
                    onChange={(e) => setSettings({
                      ...settings,
                      general: { ...settings.general, timezone: e.target.value }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option>America/New_York</option>
                    <option>America/Los_Angeles</option>
                    <option>Europe/London</option>
                    <option>Asia/Tokyo</option>
                  </select>
                </div>
                <div className="flex items-center justify-between py-3 border-t border-gray-800">
                  <div>
                    <p className="text-white font-medium">Maintenance Mode</p>
                    <p className="text-gray-500 text-sm">Temporarily disable the platform for maintenance</p>
                  </div>
                  <Switch
                    checked={settings.general.maintenanceMode}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      general: { ...settings.general, maintenanceMode: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <>
              <ChangePasswordCard />
              <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-6">Security Settings</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-white font-medium">Two-Factor Authentication</p>
                    <p className="text-gray-500 text-sm">Require 2FA for all admin users</p>
                  </div>
                  <Switch
                    checked={settings.security.twoFactorAuth}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      security: { ...settings.security, twoFactorAuth: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Session Timeout (minutes)</label>
                  <input
                    type="number"
                    value={settings.security.sessionTimeout}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: { ...settings.security, sessionTimeout: parseInt(e.target.value) }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Password Expiry (days)</label>
                  <input
                    type="number"
                    value={settings.security.passwordExpiry}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: { ...settings.security, passwordExpiry: parseInt(e.target.value) }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Max Login Attempts</label>
                  <input
                    type="number"
                    value={settings.security.maxLoginAttempts}
                    onChange={(e) => setSettings({
                      ...settings,
                      security: { ...settings.security, maxLoginAttempts: parseInt(e.target.value) }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-t border-gray-800">
                  <div>
                    <p className="text-white font-medium">IP Whitelist</p>
                    <p className="text-gray-500 text-sm">Restrict admin access to specific IPs</p>
                  </div>
                  <Switch
                    checked={settings.security.ipWhitelist}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      security: { ...settings.security, ipWhitelist: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
              </div>
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Notification Settings</h3>
              <div className="space-y-6">
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-white font-medium">Email Alerts</p>
                    <p className="text-gray-500 text-sm">Receive important alerts via email</p>
                  </div>
                  <Switch
                    checked={settings.notifications.emailAlerts}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, emailAlerts: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-white font-medium">Slack Integration</p>
                    <p className="text-gray-500 text-sm">Send notifications to Slack</p>
                  </div>
                  <Switch
                    checked={settings.notifications.slackIntegration}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, slackIntegration: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Webhook URL</label>
                  <input
                    type="text"
                    value={settings.notifications.webhookUrl}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, webhookUrl: e.target.value }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div className="flex items-center justify-between py-3 border-t border-gray-800">
                  <div>
                    <p className="text-white font-medium">Daily Report</p>
                    <p className="text-gray-500 text-sm">Receive daily summary reports</p>
                  </div>
                  <Switch
                    checked={settings.notifications.dailyReport}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      notifications: { ...settings.notifications, dailyReport: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6">API Settings</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Rate Limit (requests/min)</label>
                  <input
                    type="number"
                    value={settings.api.rateLimit}
                    onChange={(e) => setSettings({
                      ...settings,
                      api: { ...settings.api, rateLimit: parseInt(e.target.value) }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Webhook Retries</label>
                  <input
                    type="number"
                    value={settings.api.webhookRetries}
                    onChange={(e) => setSettings({
                      ...settings,
                      api: { ...settings.api, webhookRetries: parseInt(e.target.value) }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">API Version</label>
                  <select
                    value={settings.api.apiVersion}
                    onChange={(e) => setSettings({
                      ...settings,
                      api: { ...settings.api, apiVersion: e.target.value }
                    })}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400"
                  >
                    <option>v1</option>
                    <option>v2</option>
                    <option>v3</option>
                  </select>
                </div>
                <div className="flex items-center justify-between py-3 border-t border-gray-800">
                  <div>
                    <p className="text-white font-medium">Sandbox Mode</p>
                    <p className="text-gray-500 text-sm">Enable sandbox environment for testing</p>
                  </div>
                  <Switch
                    checked={settings.api.sandboxMode}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      api: { ...settings.api, sandboxMode: checked }
                    })}
                    className="data-[state=checked]:bg-teal-400"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
